import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { RentPayment } from '@/hooks/useRentPayments';
import type { Account } from '@accountHooks';

export interface RentPaymentWithAccount extends RentPayment {
  accountName?: string;
}

interface RentPaymentHistoryProps {
  rentPayments: RentPaymentWithAccount[];
  accounts?: Account[];
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatAmount(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function RentPaymentHistory({ rentPayments, accounts = [] }: RentPaymentHistoryProps) {
  // Sort by payment_date descending (defensive — data may already be ordered)
  const sorted = [...rentPayments].sort(
    (a, b) => new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime()
  );

  return (
    <Card data-testid="rent-payment-history">
      <CardHeader>
        <CardTitle>Rent Payment History</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {sorted.length === 0 ? (
          <div
            className="flex items-center justify-center py-12 text-sm text-muted-foreground"
            data-testid="empty-state"
          >
            No rent payments recorded yet.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Payment Date</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((payment) => (
                <TableRow key={payment.id} data-testid="payment-row">
                  <TableCell>{formatDate(payment.payment_date)}</TableCell>
                  <TableCell>{formatAmount(Number(payment.amount))}</TableCell>
                  <TableCell>{payment.account.nickname}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {payment.description ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
